import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, of, Subject, takeUntil, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { indicateLoading } from '@core/indicate-loading';
import { MediaService } from '@core/services/media.service';
import { SystemMessageService } from '@core/services/system-message.service';
import { TagsApi } from '@api/services/tags-api.service';
import { Customer, CustomerApi, PersonOfInteresetApi, StatisticsApi, Tag } from '@api/index';
import { IdName } from '@api/models/idname';
import { AuthService } from '@core/auth/auth.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { PersonOfInterestCreate } from '../model/poi.model';
import { FILE_TYPES } from '@app/constants';
import { TranslateConfigService } from '@core/translate-config.service';

@Component({
  selector: 'flow-person-of-interest-create',
  templateUrl: './person-of-interest-create.component.html',
  styleUrls: ['./person-of-interest-create.component.scss'],
})
export class PersonOfInterestCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @ViewChild('fileInput') fileInput: ElementRef;

  personOfInterestId: number;
  personOfInterest: PersonOfInterestCreate;
  customers: Customer[];
  categories: IdName[];
  form: FormGroup;
  selectedFile: any;
  filePreview: any;
  tags: Tag[]
  customerId: number
  isAdmin = false;
  editMode = false;
  data: any;
  supportedFileTypes = FILE_TYPES.image;
  loading$ = {
    init: new BehaviorSubject(true),
    save: new BehaviorSubject(false),
    upload: new BehaviorSubject(false)
  }

  constructor(
    private customerApi: CustomerApi,
    private tagsApi: TagsApi,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private formBuilder: FormBuilder,
    private systemMessageService: SystemMessageService,
    private personOfInterestApi: PersonOfInteresetApi,
    private statisticsServiceApi: StatisticsApi,
    private mediaService: MediaService,
    private translate: TranslateService,
    private translateConfigService: TranslateConfigService,
  ) {
    this.personOfInterestId = +this.activatedRoute.snapshot.params['id'] || null;
    this.initForm();
  }

  ngOnInit(): void {
    this.editMode = !!this.personOfInterestId
    const user = this.authService.userData;
    this.customerId = user?.currentCustomerId || user?.customerId;
    this.isAdmin = (user.roleId === UserRoleEnum.Admin) && !user.currentCustomerId;

    this.getPoi();
  }

  getPoi() {
    const customers$ = this.isAdmin ? this.customerApi.getCustomerOptions() : of([]);
    const poi$ = this.personOfInterestId ? this.personOfInterestApi.getPersonOfInterest(this.personOfInterestId) : of(null);

    forkJoin([
      customers$,
      poi$
    ]).pipe(
      takeUntil(this.destroy$),
      indicateLoading(this.loading$.init)
    ).subscribe({
      next: ([customers, poi]) => {
        if (this.isAdmin) {
          this.customers = customers;
        };

        if (poi) {
          this.personOfInterest = poi;
          const mediaFile = {
            size: poi?.size,
            name: poi?.fileName,
            id: poi?.id,
            width: poi?.width,
            height: poi?.height
          }
          this.personOfInterest.media = this.mediaService.getPoiMedia(mediaFile)
        }

        this.initForm(this.personOfInterest);
      },
      error: () => {
        console.log(this.translateConfigService.instant('notifications.error.genericError'));
      }
    });
  }

  selectFile(event: any): void {
    this.selectedFile = event;
  }

  clearFile(): void {
    this.selectedFile = null;
    this.filePreview = null;
  }

  onSaveClick(): void {
    if (!this.form.valid) {
      return;
    }
    if (this.selectedFile) {
      const customerId = this.form.get('customerId').value;
      const name = this.form.get('name').value;
      const categoryId = this.form.get('categoryId').value;
      const poi = this.form.getRawValue();
      const tags = (poi.tags || []).map((x: Tag) => ({ id: x.id || null, name: x.id ? null : x.name }));
      this.personOfInterestApi.uploadPoiImage({ customerId, name, categoryId, tags }, this.selectedFile?.selectedFile)
        .pipe(
          takeUntil(this.destroy$),
          indicateLoading(this.loading$.upload))
        .subscribe(() => {
          this.systemMessageService.success(
            this.translate.instant('notifications.success.personOfInterestCreate', { name: this.form.get('name')?.value || '' })
          );
          this.router.navigate(['/person-of-interest']);
        });
    } else {
      this.personOfInterestId && this.savePersonOfInterest(this.personOfInterest.id);
    }
  }

  onCancel() {
    this.router.navigate(['/person-of-interest'])
  }

  displayTag(tag: Tag): string {
    return tag?.name ?? '';
  }

  newTag(name: string): Tag {
    return { id: null, name };
  }

  private savePersonOfInterest(poiId): void {
    const poi = this.form.getRawValue()
    // The new Tags must not have Id but the already crated ones must not have name
    let tags = (poi.tags || []).map(x => ({
      id: x.id || null,
      name: x.id ? null : x.name
    }));

    this.personOfInterestApi.createOrUpdatePersonOfInterest(poiId, { ...poi, tags })
      .pipe(
        takeUntil(this.destroy$))
      .subscribe(() => {
        this.systemMessageService.success(
          this.translate.instant('notifications.success.personOfInterestSaved', { name: this.form.get('name')?.value || '' })
        );
        this.router.navigate(['/person-of-interest'])
      });
  }

  private initForm(personOfInterest?: PersonOfInterestCreate): void {
    this.form = this.formBuilder.group({
      id: this.personOfInterestId,
      name: [personOfInterest?.name, Validators.required],
      customerId: personOfInterest?.customer?.id || this.customerId,
      categoryId: [personOfInterest?.category.id, Validators.required],
      mediaId: personOfInterest?.media?.id,
      tags: this.formBuilder.control(personOfInterest?.tags),
      confirmation: false
    });
    this.form.get('customerId').valueChanges.pipe(
      takeUntil(this.destroy$),
      switchMap(customerId => forkJoin([
        customerId ? this.statisticsServiceApi.getStatisticsCategories({ customerIds: [customerId] }) : of(null),
        customerId ? this.tagsApi.getTags({ customerIds: [customerId] }) : of(null)
      ])),
    ).subscribe(([categories, tags]) => {
      this.categories = categories
      this.tags = tags
    });

    if (this.personOfInterestId || !personOfInterest) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

